import React, { useCallback, useEffect, useState } from 'react';
import { useShop } from '@/contexts/ShopContext';
import { useEffects } from '@/contexts/EffectsContext';
import { useSound } from '@/contexts/SoundContext';
import PixelIcon from '@/components/ui/PixelIcon';
import type { PixelIconName } from '@/components/ui/PixelIcon';
import { COLOR } from '@/config/designTokens';
import type { PowerupType } from '@/types/game';

interface PowerupDisplayProps {
  onUsePowerup?: (powerupId: string) => void;
}

export const PowerupDisplay: React.FC<PowerupDisplayProps> = ({ onUsePowerup }) => {
  const { shopState, activatePowerUp, shopPowerups } = useShop();
  const { triggerEffect } = useEffects();
  const {
    playShield, playInvincibility, playTeleport, playDoubleCoins,
    playMagnetCoins, playExtraLife, playSkipLevel, playHint,
    playUndo, playSlowTime, playPowerup,
  } = useSound();
  const [pressedKeys, setPressedKeys] = useState<Set<string>>(new Set());

  // Map powerup IDs to their specific sound functions
  const abilitySoundMap: Record<string, () => Promise<void>> = {
    shield: playShield,
    invincibility: playInvincibility,
    teleport: playTeleport,
    doubleCoins: playDoubleCoins,
    magnetCoins: playMagnetCoins,
    extraLife: playExtraLife,
    skipLevel: playSkipLevel,
    hint: playHint,
    undo: playUndo,
    slowTime: playSlowTime,
  };

  // Map powerup icon strings to PixelIcon names
  const iconMap: Record<string, PixelIconName> = {
    shield: 'shield',
    star: 'star',
    coin: 'coin',
    clock: 'clock',
    portal: 'portal',
    magnet: 'magnet',
    heart: 'heart',
    skip: 'skip',
    hint: 'hint',
    undo: 'undo',
  };

  // Filter powerups to show only consumable ones with count > 0
  const availablePowerups = Object.entries(shopState.powerups)
    .filter(([id, count]) => {
      const powerup = shopPowerups[id as PowerupType];
      return count > 0 && powerup?.category === 'consumable';
    })
    .slice(0, 4); // Show max 4 powerups

  // Handle powerup activation
  const activatePowerup = useCallback((powerupId: string) => {
    if (activatePowerUp(powerupId)) {
      // Trigger visual effect
      triggerEffect({
        type: 'glow',
        position: [80, 80],
        color: COLOR.primary,
        duration: 1000
      });

      // Play ability-specific sound (fallback to generic powerup sound)
      const soundFn = abilitySoundMap[powerupId] || playPowerup;
      soundFn();

      // Notify parent component
      onUsePowerup?.(powerupId);
    }
  }, [activatePowerUp, triggerEffect, onUsePowerup]);

  // Keyboard controls for powerups (1-4 keys)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      if (key >= '1' && key <= '4') {
        const index = parseInt(key) - 1;
        if (availablePowerups[index]) {
          setPressedKeys(prev => new Set(prev).add(key));
          activatePowerup(availablePowerups[index][0]);
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const key = e.key;
      setPressedKeys(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [availablePowerups, activatePowerup]);

  if (availablePowerups.length === 0) {
    return null;
  }

  return (
    <div className="powerup-display">
      {availablePowerups.map(([powerupId, count], index) => {
        const powerup = shopPowerups[powerupId as PowerupType];
        const keyNumber = index + 1;
        const isPressed = pressedKeys.has(keyNumber.toString());
        const pixelIconName = iconMap[powerup?.icon ?? ''];

        return (
          <div
            key={powerupId}
            className={`powerup-item ${isPressed ? 'pressed' : ''}`}
            onClick={() => activatePowerup(powerupId)}
            title={powerup?.description}
          >
            <div className="powerup-icon">
              {pixelIconName ? (
                <PixelIcon name={pixelIconName} size={24} color={COLOR.textWhite} />
              ) : (
                <PixelIcon name="star" size={24} color={COLOR.textWhite} />
              )}
            </div>

            {/* Show count */}
            {count > 0 && (
              <div className="powerup-count">{count}</div>
            )}

            {/* Show hotkey */}
            <div className="powerup-hotkey">{keyNumber}</div>
          </div>
        );
      })}
    </div>
  );
};

export default PowerupDisplay;
